import React from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

import { brandRamp, purpleRamp } from '../../bundles/MarketingInsights/styles/chartTokens';
import { resolveToken } from './themeTokens';

const ViewsConversionCard = ({ data }) => {
  const series = data?.series || [];
  const totals = data?.totals || {};
  const barColor = brandRamp()[0];        // Setlist Brand 600 (#1982C4) — bar/progress fill
  const lineColor = purpleRamp().primary; // Setlist Purple (#6938EF)
  const periodNoun = { day: 'day', week: 'week', month: 'month' }[data?.granularity] || 'day';
  const tokens = React.useMemo(() => ({
    bgPrimary: resolveToken('--sl-bg-primary', '#FFFFFF'),
    textPrimary: resolveToken('--sl-text-primary', '#181D27'),
    borderSecondary: resolveToken('--sl-border-secondary', '#E9EAEB'),
    axisText: resolveToken('--sl-text-quaternary', '#717680'),
  }), []);

  return (
    <div className="mi-card mi-viewsconv-card">
      <div className="mi-viewsconv-card__head">
        <h3 className="mi-viewsconv-card__title">Views &amp; conversion</h3>
        <p className="mi-viewsconv-card__subtitle">
          {(totals.views || 0).toLocaleString()} views
          {' · '}{(totals.orders || 0).toLocaleString()} orders
          {' · '}{totals.conversion_rate || 0}% conversion
          {' · '}{totals.window_label || 'since announce'}
        </p>
      </div>

      {series.length === 0 ? (
        <div className="mi-chart-empty">No page-view data yet.</div>
      ) : (
        <>
          <p className="mi-viewsconv-card__hint">Views per {periodNoun}</p>
          <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={series} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid stroke={tokens.borderSecondary} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: tokens.axisText }} tickLine={false} axisLine={false} />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: tokens.axisText }}
              tickLine={false}
              axisLine={false}
              width={40}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: tokens.axisText }}
              tickLine={false}
              axisLine={false}
              width={44}
              unit="%"
            />
            <Tooltip
              contentStyle={{
                background: tokens.bgPrimary,
                border: `1px solid ${tokens.borderSecondary}`,
                borderRadius: 6,
              }}
              labelStyle={{ color: tokens.textPrimary }}
              itemStyle={{ color: tokens.textPrimary }}
              formatter={(value, name) => (
                name === 'Conversion %'
                  ? [value == null ? '—' : `${value}%`, name]
                  : [(value || 0).toLocaleString(), name]
              )}
            />
            <Bar
              yAxisId="left"
              dataKey="views"
              name="Views"
              fill={barColor}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="conversion_rate"
              name="Conversion %"
              stroke={lineColor}
              strokeWidth={2.5}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

ViewsConversionCard.propTypes = {
  data: PropTypes.shape({
    series: PropTypes.arrayOf(PropTypes.shape({
      date: PropTypes.string,
      views: PropTypes.number,
      conversion_rate: PropTypes.number,
    })),
    totals: PropTypes.shape({
      views: PropTypes.number,
      orders: PropTypes.number,
      conversion_rate: PropTypes.number,
      window_label: PropTypes.string,
    }),
    granularity: PropTypes.string,
  }),
};

export default ViewsConversionCard;
